import {Dropzone} from '@dropbox/dig-components/dropzone';
import {Box} from '@dropbox/dig-foundations';
import {UploadFilePictogram} from '@dropbox/dig-icons/assets';
import {DragEvent, ReactNode, useRef, useState} from 'react';

const hasFiles = (event: DragEvent) => event.dataTransfer.types.includes('Files');

/**
 * Accepts files dropped anywhere on a chat by covering it with DIG's Dropzone
 * while a file is dragged over. Dropped files only go to onDropFiles.
 */
export const ChatDropTarget = ({
  label,
  disabled = false,
  onDropFiles,
  children,
}: {
  label: string;
  disabled?: boolean;
  onDropFiles: (files: File[]) => void;
  children: ReactNode;
}) => {
  const [isDragging, setIsDragging] = useState(false);
  // dragenter/dragleave fire for every child, so count depth instead of toggling.
  const depthRef = useRef(0);

  const endDrag = () => {
    depthRef.current = 0;
    setIsDragging(false);
  };

  return (
    <Box
      position="relative"
      display="flex"
      flexDirection="column"
      flexGrow={1}
      minHeight="0"
      onDragEnter={(event: DragEvent) => {
        if (disabled || !hasFiles(event)) {
          return;
        }
        depthRef.current += 1;
        setIsDragging(true);
      }}
      onDragLeave={() => {
        depthRef.current = Math.max(0, depthRef.current - 1);
        if (depthRef.current === 0) {
          setIsDragging(false);
        }
      }}
      // Dropping outside the overlay (e.g. on its padding) must not open the file.
      onDragOver={(event: DragEvent) => isDragging && event.preventDefault()}
      onDrop={(event: DragEvent) => {
        event.preventDefault();
        endDrag();
      }}
    >
      {children}
      {isDragging ? (
        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          display="flex"
          padding="16"
          backgroundColor="Background Base"
        >
          <Box display="flex" flexDirection="column" justifyContent="center" flexGrow={1}>
            <Dropzone
              ariaLabel={label}
              label={label}
              uploadProgressLabel=""
              errorLabel=""
              pictogram={UploadFilePictogram}
              hasAnimatedBorderOnHover
              onDragOver={(event) => event.preventDefault()}
              onDrop={(event) => {
                event.preventDefault();
                event.stopPropagation();
                endDrag();
                const files = Array.from(event.dataTransfer.files);
                if (files.length) {
                  onDropFiles(files);
                }
              }}
            />
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};
